import React from "react";
import { Box, Flex, Image, Text, Tooltip } from "@chakra-ui/react";
import StatusDotRed from "./Images/status-dot-red.svg";
import StatusDotGreen from "./Images/status-dot-green.svg";
import MetaDot from "./Images/meta-dot.svg";
import CardArrow from "./Images/card-arrow-cyan.svg";
import { c } from "./classroomColors";

/**
 * The redesigned assignment card, shared by the student and teacher classroom
 * dashboards (Figma nodes 70:40, 70:67, 70:79).
 *
 * A cream card carrying a white status pill, an optional dark subject badge, the
 * assignment name, an optional meta block (Created / Due, or a mark), and the
 * tilted cyan arrow in the bottom right corner.
 *
 * The Figma frames are absolutely positioned at a fixed 347px width. Here the
 * layout is flex so the card still works in the responsive dashboard grids, but
 * the spacing, type sizes and the exported artwork match the design.
 */

const DOTS = { red: StatusDotRed, green: StatusDotGreen };

const AssignmentCard = ({
  statusLabel,
  /** "red" | "green" | "none" — the classroom card's pill carries no dot. */
  statusTone = "red",
  subject,
  title,
  /** [{ label: "Due:", value: "02/08/2026" }] — label upright, value italic. */
  meta = [],
  /** ["Cambridge", "IGCSE"] — joined by the dot separator (Figma 72:109). */
  tags = [],
  /** A single fully italic line, e.g. "Mark : 45/75". */
  note,
  onClick,
  /** An extra control beside the arrow, e.g. managing who the work went to.
   *  It sits inside the card, so it stops the click reaching the card. */
  secondaryAction,
  /** A control in the top-right corner (Figma 114:50 puts the classroom's
   *  "..." menu there). Also kept from opening the card. */
  cornerAction,
  /** A strip along the bottom of the card, e.g. a classroom's marking state. */
  footer,
  isDisabled = false,
}) => (
  <Box
    position="relative"
    width="100%"
    minHeight="130px"
    borderRadius="14px"
    overflow="hidden"
    background={c.cardBg}
    padding="20px 24px 22px"
    cursor={isDisabled ? "default" : "pointer"}
    onClick={isDisabled ? undefined : onClick}
    transition="transform 0.2s, box-shadow 0.2s"
    _hover={isDisabled ? undefined : { transform: "translateY(-2px)", boxShadow: "md" }}
  >
    <Flex justifyContent="space-between" alignItems="flex-start" gap="10px" pr={cornerAction ? "28px" : 0}>
      {/* status pill */}
      <Flex
        alignItems="center"
        justifyContent="center"
        gap="10px"
        background={c.cardPill}
        borderRadius="63px"
        padding="4px 10px"
        flexShrink={0}
      >
        {statusTone !== "none" && (
          <Image src={DOTS[statusTone] || DOTS.red} alt="" width="13px" height="13px" />
        )}
        <Text fontSize="11px" fontWeight="300" color={c.text} whiteSpace="nowrap">
          {statusLabel}
        </Text>
      </Flex>

      {/* Subject badge. Names run long ("Information and Communication
          Technology"), so it is capped and truncated rather than allowed to
          push past the card, with the full name on hover. */}
      {subject && (
        <Tooltip label={subject} placement="top" hasArrow openDelay={300} fontSize="11px">
          <Flex
            alignItems="center"
            background="#404040"
            borderRadius="22px"
            padding="2px 10px"
            minWidth={0}
            maxWidth="55%"
          >
            <Text fontSize="12px" fontWeight="500" color="#ffffff" isTruncated>
              {subject}
            </Text>
          </Flex>
        </Tooltip>
      )}
    </Flex>

    <Text
      mt="16px"
      pr="40px"
      fontSize="14px"
      fontWeight="500"
      lineHeight="1.25"
      color={c.cardTitle}
      noOfLines={2}
    >
      {title}
    </Text>

    {tags.length > 0 && (
      <Flex alignItems="center" gap="4px" mt="6px" pr="44px" flexWrap="wrap">
        {tags.filter(Boolean).map((tag, i) => (
          <React.Fragment key={tag}>
            {i > 0 && <Image src={MetaDot} alt="" width="7px" height="7px" />}
            <Text fontSize="11px" fontWeight="300" color={c.text} whiteSpace="nowrap">
              {tag}
            </Text>
          </React.Fragment>
        ))}
      </Flex>
    )}

    {(meta.length > 0 || note) && (
      <Box mt="6px" pr="44px">
        {meta.map((row, i) => (
          <Text
            key={row.label}
            fontSize="11px"
            fontWeight="300"
            color={c.text}
            // The design fades everything above the last line (Created sits at
            // 54% next to a solid Due).
            opacity={i === meta.length - 1 ? 1 : 0.54}
            lineHeight="1.45"
          >
            {row.label}{" "}
            <Box as="span" fontStyle="italic">
              {row.value}
            </Box>
          </Text>
        ))}
        {note && (
          <Text fontSize="11px" fontStyle="italic" color={c.text} lineHeight="1.45">
            {note}
          </Text>
        )}
      </Box>
    )}

    {cornerAction && (
      <Flex
        position="absolute"
        top="4px"
        right="8px"
        alignItems="center"
        justifyContent="center"
        onClick={(event) => event.stopPropagation()}
      >
        {cornerAction}
      </Flex>
    )}

    {footer && (
      <Box mt="12px" pr="44px">
        {footer}
      </Box>
    )}

    {secondaryAction && (
      <Flex
        position="absolute"
        right="52px"
        bottom="12px"
        alignItems="center"
        justifyContent="center"
        onClick={(event) => event.stopPropagation()}
      >
        {secondaryAction}
      </Flex>
    )}

    {/* tilted cyan arrow, bottom right */}
    <Flex
      position="absolute"
      right="16px"
      bottom="14px"
      width="27.937px"
      height="27.524px"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Image
        src={CardArrow}
        alt=""
        width="21px"
        height="20px"
        transform="rotate(-28.04deg)"
      />
    </Flex>
  </Box>
);

export default AssignmentCard;
